import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as SubirArchivo } from '../assets/icon/subirarchivo.svg';
import { ReactComponent as Volver } from '../assets/icon/volver.svg';
import './EditarPromociones.css';
import imageService from '../services/imageService';
import { createPromocion } from '../services/promociones';
import { UPLOAD_STATUS } from '../utils';
import { useToast } from '../contexts/ToastContext';

const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const validateImageFile = (file, onError) => {
    if (!file) return false;
    if (!file.type.startsWith("image/")) {
        if (onError) onError("Solo se permiten imágenes");
        return false;
    }
    
    const MAX_SIZE = 1 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
        if (onError) onError("La imagen no puede superar 1MB");
        return false;
    }
    
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const { width, height } = img;
            const MIN_WIDTH = 768;
            const MIN_HEIGHT = 512;
            const MAX_WIDTH = 1920;
            const MAX_HEIGHT = 1280;
            
            if (width < MIN_WIDTH || height < MIN_HEIGHT) {
                if (onError) onError(`La imagen debe ser mínimo ${MIN_WIDTH}x${MIN_HEIGHT} pixels`);
                resolve(false);
            } else if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                if (onError) onError(`La imagen no puede exceder ${MAX_WIDTH}x${MAX_HEIGHT} pixels`);
                resolve(false);
            } else {
                resolve(true);
            }
        };
        img.onerror = () => {
            if (onError) onError("Error al cargar la imagen");
            resolve(false);
        };
        img.src = URL.createObjectURL(file);
    });
};

const useImageUpload = () => {
    const [preview, setPreview] = useState(null);
    const [imageData, setImageData] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageUrl, setImageUrl] = useState("");
    const [imageFileName, setImageFileName] = useState("");
    const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState("");

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        
        const isValid = await validateImageFile(file, (error) => {
            setUploadError(error);
            setUploadStatus(UPLOAD_STATUS.ERROR);
        });
        
        if (!isValid) return;
        
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
        setUploadStatus(UPLOAD_STATUS.IDLE);
        setUploadError("");
        
        try {
            setUploadStatus(UPLOAD_STATUS.UPLOADING);
            const result = await imageService.upload(file, {
                onProgress: setUploadProgress
            });
            
            if (result.success) {
                setImageUrl(result.url);
                setImageFileName(result.fileName);
                setImageData(result.url);
                setUploadStatus(UPLOAD_STATUS.SUCCESS);
            } else {
                throw new Error(result.message || 'Error al subir imagen');
            }
        } catch (error) {
            setUploadError(error.message);
            setUploadStatus(UPLOAD_STATUS.ERROR);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files[0]);
    }, [handleFile]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
    }, []);

    const removeImage = useCallback(async () => {
        if (imageFileName) {
            try {
                await imageService.deleteImage(imageFileName);
            } catch (error) {
                console.warn('No se pudo eliminar la imagen del servidor:', error.message);
            }
        }
        setPreview(null);
        setImageData("");
        setSelectedFile(null);
        setImageUrl("");
        setImageFileName("");
        setUploadStatus(UPLOAD_STATUS.IDLE);
        setUploadError("");
        setUploadProgress(0);
    }, [imageFileName]);

    return {
        preview,
        imageData,
        selectedFile,
        imageUrl,
        imageFileName,
        uploadStatus,
        uploadProgress,
        uploadError,
        handleFile,
        handleDrop,
        handleDragOver,
        removeImage
    };
};

const useFormValidation = (formData) => {
    const [errors, setErrors] = useState({});

    const validateForm = useCallback(() => {
        const newErrors = {};
        
        if (!formData.pTitulo?.trim()) {
            newErrors.pTitulo = "El título es requerido";
        }
        
        if (!formData.pImagen?.trim()) {
            newErrors.pImagen = "La imagen es requerida";
        }
        
        if (!formData.pFecha_inicio) {
            newErrors.pFecha_inicio = "La fecha de inicio es requerida";
        }
        
        if (!formData.pFecha_fin) {
            newErrors.pFecha_fin = "La fecha de fin es requerida";
        }
        
        if (!formData.pUbicacion?.trim()) {
            newErrors.pUbicacion = "La ubicación es requerida";
        }
        
        if (!formData.pDescripcion?.trim()) {
            newErrors.pDescripcion = "La descripción es requerida";
        }
        
        if (!formData.pValor_descuento?.trim()) {
            newErrors.pValor_descuento = "El valor del descuento es requerido";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    const clearError = useCallback((fieldName) => {
        setErrors(prev => ({
            ...prev,
            [fieldName]: ""
        }));
    }, []);

    return {
        errors,
        validateForm,
        clearError
    };
};

const ImageInfoModal = ({ isOpen, onClose }) => {
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="info-modal" ref={modalRef}>
                <h3>Medidas de Imagen Soportadas</h3>
                <ul>
                    <li><strong>Ancho:</strong> 768 - 1920 pixels</li>
                    <li><strong>Alto:</strong> 512 - 1280 pixels</li>
                    <li><strong>Peso máximo:</strong> 1MB</li>
                    <li><strong>Formatos:</strong> JPEG, PNG</li>
                </ul>
                <button className="modal-close-btn" onClick={onClose}>Cerrar</button>
            </div>
        </div>
    );
};

function AgregarPromociones() {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    
    const [formData, setFormData] = useState(() => ({
        pIdPromocion: null,
        pTitulo: "",
        pImagen: "",
        pFecha_inicio: getCurrentDateTime(),
        pFecha_fin: getCurrentDateTime(),
        pUbicacion: "",
        pMes: "",
        pDescripcion: "",
        pValor_descuento: "",
        pEstado: "activa"
    }));
    
    const [loading, setLoading] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);

    const { 
        preview, 
        imageData, 
        selectedFile,
        imageUrl,
        imageFileName,
        uploadStatus,
        uploadProgress,
        uploadError,
        handleFile, 
        handleDrop, 
        handleDragOver, 
        removeImage 
    } = useImageUpload();
    const { errors, validateForm, clearError } = useFormValidation(formData);

    useEffect(() => {
        if (uploadStatus === UPLOAD_STATUS.SUCCESS) {
            setFormData(prev => ({
                ...prev,
                pImagen: imageUrl,
                pImagenFileName: imageFileName
            }));
        }
    }, [imageUrl, imageFileName, uploadStatus]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        clearError(name);
    }, [clearError]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);
        try {
            const promocionData = {
                Titulo: formData.pTitulo,
                Fecha_inicio: formData.pFecha_inicio,
                Fecha_fin: formData.pFecha_fin,
                Ubicacion: formData.pUbicacion,
                Mes: formData.pMes,
                Descripcion: formData.pDescripcion,
                Valor_descuento: formData.pValor_descuento,
                Estado: formData.pEstado,
                Url_File: formData.pImagen,
                FileName: formData.pImagenFileName
            };

            const result = await createPromocion(promocionData);

            if (result.success && result.error === 0) {
                showSuccess('Promoción creada correctamente');
                navigate("/dashboard/Promociones");
            } else {
                showError(result.mensaje || 'Error al guardar la promoción');
            }
        } catch (error) {
            console.error('Error saving promotion:', error);
            showError('Error al guardar la promoción');
        } finally {
            setLoading(false);
        }
    }, [formData, validateForm, navigate, showSuccess, showError]);

    const submitButtonText = useMemo(() => {
        return loading ? 'Guardando...' : 'Guardar';
    }, [loading]);

    return (
        <div className='bodyAgregarPromociones'>
            <div className='tituloAgregarPromociones'>
                <p>-Nueva Promoción</p>
            </div>
            
            <div className='cuerpoBotonVolverPromociones'>
                <button
                    className='botonesAgregarPromociones'
                    onClick={() => navigate("/dashboard/Promociones")}
                    type="button"
                >
                    <div className="svgBotones">
                        <Volver className="logo-svgBotones" />
                    </div>
                    Volver
                </button>
            </div>

            <form onSubmit={handleSubmit} className='cuerpoAgregarPromociones'>

                <div className="logoUpload">
                    <div className="label-with-info">
                        <p className="label">Imagen</p>
                        <button 
                            id="empresa-info-btn" 
                            className="info-button" 
                            type="button" 
                            title="Ver medidas soportadas"
                            onClick={() => setShowInfoModal(true)}
                        >
                            ?
                        </button>
                    </div>
                    <div
                        className={`uploadBox ${errors.pImagen ? 'error' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        {preview ? (
                            <>
                                <img src={preview} alt="preview" className="preview" />
                                <div className="actions">
                                    <label className="actionButton cambiar">
                                        Cambiar
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFile(e.target.files[0])}
                                            hidden
                                        />
                                    </label>
                                    <button type="button" className="actionButton eliminar" onClick={removeImage}>
                                        Eliminar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="iconSubirArchivo">
                                    <div className="svgSubirArchivo">
                                        <SubirArchivo className="logosvgSubirArchivo" />
                                    </div>
                                </div>
                                <p>Seleccione o arrastre aquí los archivos o</p>
                                <label className="link">
                                    haga click para seleccionar
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFile(e.target.files[0])}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                <p>(Peso m&aacute;ximo 1MB)</p>
                            </>
                        )}
                        {errors.pImagen && <span className="error-text">{errors.pImagen}</span>}
                    </div>
                </div>

                <div className='inputsPromociones'>
                    
                    <div className={`input-groupAgregarPromociones ${errors.pTitulo ? 'error' : ''}`}>
                        <div className='text-inputAgregarPromociones'>
                            <label htmlFor="pTitulo">Título</label>
                        </div>
                        <input
                            className="inputAgregarPromociones"
                            type="text"
                            id="pTitulo"
                            name="pTitulo"
                            value={formData.pTitulo}
                            onChange={handleChange}
                            placeholder="Cyber Monday 50%"
                            maxLength={200}
                            disabled={loading}
                        />
                        <div className="contador">{formData.pTitulo.length}/200</div>
                        {errors.pTitulo && <span className="error-text">{errors.pTitulo}</span>}
                    </div>

                    <div className={`calendario ${errors.pFecha_inicio ? 'error' : ''}`}>
                        <label>Fecha Inicio</label>
                        <input
                            type="datetime-local"
                            name="pFecha_inicio"
                            value={formData.pFecha_inicio}
                            onChange={handleChange}
                            className={`inputAgregarPromociones ${errors.pFecha_inicio ? 'error' : ''}`}
                            disabled={loading}
                        />
                        {errors.pFecha_inicio && <span className="error-text">{errors.pFecha_inicio}</span>}
                    </div>
                    
                    <div className={`calendario ${errors.pFecha_fin ? 'error' : ''}`}>
                        <label>Fecha Fin</label>
                        <input
                            type="datetime-local"
                            name="pFecha_fin"
                            value={formData.pFecha_fin}
                            onChange={handleChange}
                            className={`inputAgregarPromociones ${errors.pFecha_fin ? 'error' : ''}`}
                            disabled={loading}
                        />
                        {errors.pFecha_fin && <span className="error-text">{errors.pFecha_fin}</span>}
                    </div>

                    <div className={`input-groupAgregarPromociones ${errors.pUbicacion ? 'error' : ''}`}>
                        <div className='text-inputAgregarPromociones'>
                            <label htmlFor="pUbicacion">Ubicación</label>
                        </div>
                        <input
                            className="inputAgregarPromociones"
                            type="text"
                            id="pUbicacion"
                            name="pUbicacion"
                            value={formData.pUbicacion}
                            onChange={handleChange}
                            placeholder="Tienda online"
                            maxLength={200}
                            disabled={loading}
                        />
                        {errors.pUbicacion && <span className="error-text">{errors.pUbicacion}</span>}
                    </div>

                    <div className='input-groupAgregarPromociones'>
                        <div className='text-inputAgregarPromociones'>
                            <label htmlFor="pMes">Mes</label>
                        </div>
                        <select
                            className="inputAgregarPromociones"
                            id="pMes"
                            name="pMes"
                            value={formData.pMes}
                            onChange={handleChange}
                            disabled={loading}
                        >
                            <option value="">Seleccione un mes</option>
                            <option value="1">Enero</option>
                            <option value="2">Febrero</option>
                            <option value="3">Marzo</option>
                            <option value="4">Abril</option>
                            <option value="5">Mayo</option>
                            <option value="6">Junio</option>
                            <option value="7">Julio</option>
                            <option value="8">Agosto</option>
                            <option value="9">Septiembre</option>
                            <option value="10">Octubre</option>
                            <option value="11">Noviembre</option>
                            <option value="12">Diciembre</option>
                        </select>
                    </div>

                    <div className={`input-groupAgregarPromociones ${errors.pValor_descuento ? 'error' : ''}`}>
                        <div className='text-inputAgregarPromociones'>
                            <label htmlFor="pValor_descuento">Valor del Descuento</label>
                        </div>
                        <input
                            className="inputAgregarPromociones"
                            type="number"
                            id="pValor_descuento"
                            name="pValor_descuento"
                            value={formData.pValor_descuento}
                            onChange={handleChange}
                            placeholder="50.00"
                            step="0.01"
                            min="0"
                            max="100"
                            disabled={loading}
                        />
                        {errors.pValor_descuento && <span className="error-text">{errors.pValor_descuento}</span>}
                    </div>

                    <div className='input-groupAgregarPromociones'>
                        <div className='text-inputAgregarPromociones'>
                            <label htmlFor="pEstado">Estado</label>
                        </div>
                        <select
                            className="inputAgregarPromociones"
                            id="pEstado"
                            name="pEstado"
                            value={formData.pEstado}
                            onChange={handleChange}
                            disabled={loading}
                        >
                            <option value="activa">Activa</option>
                            <option value="inactiva">Inactiva</option>
                            <option value="proxima">Próxima</option>
                            <option value="vencida">Vencida</option>
                        </select>
                    </div>

                    <div className={`input-groupAgregarPromociones ${errors.pDescripcion ? 'error' : ''}`}>
                        <div className='text-inputAgregarPromociones'>
                            <label htmlFor="pDescripcion">Descripción</label>
                        </div>
                        <textarea
                            className="AgregarPromocionestextarea"
                            id="pDescripcion"
                            name="pDescripcion"
                            value={formData.pDescripcion}
                            onChange={handleChange}
                            placeholder="50% de descuento en toda la web por 24 horas"
                            maxLength={500}
                            rows={4}
                            disabled={loading}
                        />
                        <div className="contador">{formData.pDescripcion.length}/500</div>
                        {errors.pDescripcion && <span className="error-text">{errors.pDescripcion}</span>}
                    </div>

                    {errors.submit && <span className="error-text submit-error">{errors.submit}</span>}

                    {uploadStatus !== UPLOAD_STATUS.IDLE && (
                        <div className="upload-status">
                            {uploadStatus === UPLOAD_STATUS.UPLOADING && (
                                <div className="progress-container">
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="progress-text">Subiendo imagen... {uploadProgress}%</p>
                                </div>
                            )}
                            
                            {uploadStatus === UPLOAD_STATUS.SUCCESS && (
                                <div className="success-message">
                                    <p>✓ Imagen subida exitosamente</p>
                                    <input type="hidden" name="pImagenUrl" value={imageUrl} />
                                    <input type="hidden" name="pImagenFileName" value={imageFileName} />
                                </div>
                            )}
                            
                            {uploadStatus === UPLOAD_STATUS.ERROR && (
                                <div className="error-message">
                                    <p>✗ {uploadError}</p>
                                    <button 
                                        type="button" 
                                        className="retry-button"
                                        onClick={() => {
                                            if (selectedFile) handleFile(selectedFile);
                                        }}
                                    >
                                        Reintentar
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <div className='botonesAgregarPromociones2'>
                        <button 
                            type="submit" 
                            className="btn-acceptConfirmarPromociones"
                            disabled={loading || uploadStatus === UPLOAD_STATUS.UPLOADING}
                        >
                            {submitButtonText}
                        </button>
                    </div>
                </div>
            </form>

            <ImageInfoModal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} />
        </div>
    );
}

export default AgregarPromociones;
